import type { GameStore } from '../core/store';
import { getSpecies } from '../game/data/species';
import { isFainted, maxHp, type LumineInstance } from '../game/lumine';
import { battlerUrl } from './creatureArt';
import { el } from './dom';

/**
 * Pannello squadra, in due modalità:
 *  - 'menu': consultazione + azioni di gioco (salva, esci)
 *  - 'pick': scelta di un Lumine in grado di lottare (cambio in battaglia)
 */

function hpRow(lumine: LumineInstance): HTMLDivElement {
  const species = getSpecies(lumine.speciesId);
  const row = el('div', 'team-row');
  if (isFainted(lumine)) row.classList.add('fainted');
  const img = el('img');
  img.src = battlerUrl(lumine.speciesId);
  img.alt = species.name;
  const info = el('div', 'info');
  const max = maxHp(lumine);
  info.append(
    el('div', 'nome', `${species.name}  Lv.${lumine.level}  —  ${lumine.hp}/${max} PS`),
  );
  const bar = el('div', 'hp-bar');
  const fill = el('div', 'fill');
  const ratio = lumine.hp / max;
  fill.style.width = `${ratio * 100}%`;
  if (ratio <= 0.25) fill.classList.add('low');
  else if (ratio <= 0.5) fill.classList.add('warn');
  bar.appendChild(fill);
  info.appendChild(bar);
  row.append(img, info);
  return row;
}

export interface MenuActions {
  onSave: () => void;
  onNewGame: () => void;
}

export function openTeamMenu(
  uiRoot: HTMLElement,
  store: GameStore,
  actions: MenuActions,
): Promise<void> {
  return new Promise((resolve) => {
    const overlay = el('div', 'overlay');
    const panel = el('div', 'panel');
    panel.append(el('h2', 'ui-title', 'Squadra'));

    const list = el('div', 'team-list');
    for (const lumine of store.state.player.team) list.appendChild(hpRow(lumine));
    if (store.state.player.team.length === 0) {
      list.appendChild(el('p', undefined, 'Non hai ancora nessun Lumine.'));
    }
    panel.appendChild(list);

    const actionsRow = el('div', 'menu-actions');
    const saveBtn = el('button', 'btn primary', '💾 Salva');
    saveBtn.addEventListener('click', () => {
      actions.onSave();
      close();
    });
    const newBtn = el('button', 'btn danger', '🗑 Nuova partita');
    newBtn.addEventListener('click', () => {
      if (window.confirm('Cancellare il salvataggio e ricominciare?')) {
        actions.onNewGame();
      }
    });
    const closeBtn = el('button', 'btn', '✕ Chiudi');
    closeBtn.addEventListener('click', close);
    actionsRow.append(saveBtn, newBtn, closeBtn);
    panel.appendChild(actionsRow);

    overlay.appendChild(panel);
    uiRoot.appendChild(overlay);

    function close(): void {
      overlay.remove();
      resolve();
    }
  });
}

/** Scelta obbligata o annullabile di un membro della squadra (per il cambio). */
export function pickTeamMember(
  uiRoot: HTMLElement,
  team: LumineInstance[],
  options: { title: string; cancellable: boolean; excludeIndex?: number },
): Promise<number | null> {
  return new Promise((resolve) => {
    const overlay = el('div', 'overlay');
    const panel = el('div', 'panel');
    panel.append(el('h2', 'ui-title', options.title));
    const list = el('div', 'team-list');

    team.forEach((lumine, index) => {
      const row = hpRow(lumine);
      const selectable = !isFainted(lumine) && index !== options.excludeIndex;
      if (selectable) {
        row.style.cursor = 'pointer';
        row.addEventListener('click', () => {
          overlay.remove();
          resolve(index);
        });
      }
      list.appendChild(row);
    });
    panel.appendChild(list);

    if (options.cancellable) {
      const cancel = el('button', 'btn', 'Annulla');
      cancel.addEventListener('click', () => {
        overlay.remove();
        resolve(null);
      });
      const actionsRow = el('div', 'menu-actions');
      actionsRow.appendChild(cancel);
      panel.appendChild(actionsRow);
    }

    overlay.appendChild(panel);
    uiRoot.appendChild(overlay);
  });
}
